import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProperty } from '@features/properties';
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Card } from "@shared/components/ui/card";
import { Skeleton } from "@shared/components/ui/skeleton";
import {
  Bed, Bath, Maximize, MapPin, Calendar, Home,
  ChevronLeft, ChevronRight, Check, ArrowRight
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getAllResponsiveImageProps, PLACEHOLDER_IMAGE, IMAGE_SIZES } from '@/types';

export default function PropertyDetails() {
  const { t, i18n } = useTranslation();
  const { id: propertyId } = useParams<{ id: string }>();
  const [currentImage, setCurrentImage] = useState(0);

  const { data: property, isLoading } = useProperty(propertyId);

  // Reset gallery position when property changes
  useEffect(() => {
    setCurrentImage(0);
  }, [propertyId]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-SA' : 'en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Memoize gallery images with responsive props to avoid recalculating on every render
  const images = useMemo(
    () => property
      ? getAllResponsiveImageProps(property.photos, 'gallery')
      : [{ src: PLACEHOLDER_IMAGE, srcSet: '', sizes: IMAGE_SIZES.gallery }],
    [property?.photos]
  );

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-[500px] rounded-2xl mb-8" />
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32" />
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('properties.notFound')}</h2>
          <Link to="/">
            <Button>{t('properties.backToHome')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Build location string
  const locationParts = [];
  if (property.address) {
    locationParts.push(property.address);
  } else {
    if (property.district?.label) {
      locationParts.push(property.district.label);
    }
    locationParts.push(property.city.label);
  }
  const locationString = locationParts.join('، ');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Image Gallery */}
      <div className="relative w-full h-[70vh] max-h-[600px] bg-slate-900">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImage}
            src={images[currentImage].src}
            srcSet={images[currentImage].srcSet}
            sizes={images[currentImage].sizes}
            alt={property.title}
            className="w-full h-full object-cover"
            // @ts-expect-error - fetchpriority is valid HTML but not in React types yet
            fetchpriority={currentImage === 0 ? 'high' : 'auto'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

        {/* Navigation Arrows - only show if multiple images */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              aria-label={t('a11y.previousImage')}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              aria-label={t('a11y.nextImage')}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Image Dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2" role="tablist">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  role="tab"
                  aria-selected={currentImage === index}
                  aria-label={t('a11y.imageOf', { current: index + 1, total: images.length })}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentImage === index ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Back Button */}
        <Link to="/" className="absolute top-4 right-4">
          <Button variant="secondary" className="bg-white/90 hover:bg-white">
            <ArrowRight className="w-4 h-4 ml-2" />
            {t('common.back')}
          </Button>
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="md:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge className="mb-3 bg-emerald-100 text-emerald-700 border-0">
                  {property.propertyType.label}
                </Badge>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                  {property.title}
                </h1>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-5 h-5" />
                  <span>{locationString}</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl">
                <Bed className="w-5 h-5 text-slate-600" />
                <span className="font-medium">{property.bedrooms} {t('properties.bedrooms')}</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl">
                <Bath className="w-5 h-5 text-slate-600" />
                <span className="font-medium">{property.bathrooms} {t('properties.bathrooms')}</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl">
                <Maximize className="w-5 h-5 text-slate-600" />
                <span className="font-medium">{property.area} {t('properties.sqm')}</span>
              </div>
              {property.yearBuilt && (
                <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl">
                  <Calendar className="w-5 h-5 text-slate-600" />
                  <span className="font-medium">{t('properties.builtIn')} {property.yearBuilt}</span>
                </div>
              )}
            </div>

            {/* Features */}
            {property.features?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('properties.features')}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {property.features.map((feature) => (
                    <div key={feature.code} className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-slate-700">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Price Card */}
          <div>
            <Card className="p-6 border-0 shadow-xl sticky top-4">
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-1">{t('properties.price')}</p>
                <p className="text-3xl font-bold text-emerald-600">
                  {formatPrice(property.price)}
                </p>
              </div>

              <div className="space-y-3 mb-6 pb-6 border-b border-slate-100">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{t('properties.monthlyPayment')}</span>
                  <span className="font-medium">{formatPrice(Math.round(property.price * 0.005))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{t('properties.financeDuration')}</span>
                  <span className="font-medium">25 {t('properties.years')}</span>
                </div>
              </div>

              <Link to={`/purchase/${property.id}`}>
                <Button className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium">
                  {t('properties.buyNow')}
                </Button>
              </Link>

              <p className="text-center text-xs text-slate-400 mt-4">
                {t('properties.fullyDigital')}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
